<template>
  <div class="test-character-page">
    <header class="test-character-header">
      <div>
        <p class="eyebrow">Test Character</p>
        <h1>角色风格化测试</h1>
        <p class="summary">
          可上传角色图片做风格化，也可只填文字设定直接设计角色形象。
        </p>
      </div>
      <button type="button" @click="router.push({ name: 'create' })">返回创作</button>
    </header>

    <main class="test-character-content">
      <section class="panel input-panel">
        <div class="section-title">
          <h2>上传角色图（可选）</h2>
          <span>{{
            uploadedImageUrl ? '已上传 · 风格化' : '未上传 · 文字设计'
          }}</span>
        </div>

        <div class="input-grid">
          <el-upload
            ref="uploadRef"
            class="image-upload"
            :class="{ 'is-uploaded': previewImageUrl, 'is-uploading': uploading }"
            :auto-upload="false"
            :show-file-list="false"
            :disabled="uploading || generating || looping"
            accept="image/*"
            drag
            :on-change="handleUploadChange"
          >
            <div class="upload-content" :class="{ 'has-image': previewImageUrl }">
              <template v-if="previewImageUrl">
                <img class="uploaded-preview-image" :src="previewImageUrl" alt="上传角色预览" />
                <span class="change-upload-button">{{
                  uploading ? '图片上传中...' : '点击更换图片'
                }}</span>
              </template>
              <template v-else>
                <span class="upload-icon">+</span>
                <strong>{{ uploading ? '图片上传中...' : '点击或拖拽上传图片（可选）' }}</strong>
                <small>不上传则根据名称与描述文字设计角色形象</small>
              </template>
            </div>
          </el-upload>

          <div class="form-card">
            <label>
              <span>{{ uploadedImageUrl ? '角色名称（可选）' : '角色名称' }}</span>
              <el-input
                v-model="characterName"
                placeholder="如：乐乐"
                :disabled="generating || looping"
              />
            </label>
            <label>
              <span>{{ uploadedImageUrl ? '补充描述（可选）' : '角色设定' }}</span>
              <el-input
                v-model="characterDescription"
                type="textarea"
                resize="none"
                placeholder="如：短发、大眼睛、蓝色条纹上衣"
                :disabled="generating || looping"
              />
            </label>
            <label>
              <span>目标绘本风格</span>
              <el-select
                v-model="selectedStyle"
                :disabled="generating || looping"
                class="style-select"
              >
                <el-option
                  v-for="style in artStyles"
                  :key="style.value"
                  :label="style.label"
                  :value="style.value"
                />
              </el-select>
            </label>

            <div class="actions">
              <button
                class="primary-action"
                type="button"
                :disabled="!canGenerate"
                @click="generateSelectedStyle"
              >
                {{ generating ? progressText : '生成当前风格' }}
              </button>
              <button
                class="secondary-action"
                type="button"
                :disabled="!canLoop"
                @click="generateAllStyles"
              >
                {{ looping ? `循环生成中 ${loopIndex}/${artStyles.length}` : '一键循环风格' }}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section class="panel result-panel">
        <div class="section-title">
          <h2>生成结果</h2>
          <span>{{
            generatedResults.length ? `${generatedResults.length} 张结果` : '暂无结果'
          }}</span>
        </div>

        <div v-if="currentResult" class="current-result-card">
          <div class="image-box result-image">
            <img :src="currentResult.imageUrl" :alt="`${currentResult.styleLabel}角色图`" />
          </div>
          <div class="result-copy">
            <p class="result-style">{{ currentResult.styleLabel }}</p>
            <p v-if="currentResult.prompt" class="result-prompt">{{ currentResult.prompt }}</p>
            <button type="button" @click="downloadResult(currentResult)">下载图片</button>
          </div>
        </div>

        <div v-else class="empty-result">
          <h3>等待生成</h3>
          <p>
            上传图片后可风格化；不上传则填写名称与设定后直接设计角色形象。支持单风格或一键循环全部风格。
          </p>
        </div>
      </section>

      <section class="panel gallery-panel">
        <div class="section-title">
          <h2>全部风格图片</h2>
          <button
            class="download-all-button"
            type="button"
            :disabled="!generatedResults.length"
            @click="downloadAllResults"
          >
            一键下载
          </button>
        </div>

        <div v-if="generatedResults.length" class="result-grid">
          <article
            v-for="result in generatedResults"
            :key="result.style"
            class="result-card"
            :class="{ active: result.style === currentResult?.style }"
            @click="currentStyle = result.style"
          >
            <div class="image-box grid-image">
              <img :src="result.imageUrl" :alt="`${result.styleLabel}角色图`" />
            </div>
            <div class="grid-copy">
              <strong>{{ result.styleLabel }}</strong>
              <button type="button" @click.stop="downloadResult(result)">下载</button>
            </div>
          </article>
        </div>

        <div v-else class="empty-gallery">
          <span>生成后的风格图片会展示在这里</span>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, ref } from 'vue';
  import { ElMessage } from 'element-plus';
  import type { UploadFile, UploadInstance } from 'element-plus';
  import { useRouter } from 'vue-router';
  import { uploadCharacterReferenceImage } from '@/api/character';
  import {
    designCharacter,
    pollTask,
    stylizeCharacter,
    type CharacterDesignResult,
    type CharacterStylizeResult,
    type TaskSnapshot
  } from '@/api/storybook-direct';
  import { useBalanceStore } from '@/store';

  interface ArtStyleOption {
    value: string;
    label: string;
  }

  interface GeneratedResult {
    style: string;
    styleLabel: string;
    imageUrl: string;
    prompt: string;
  }

  const router = useRouter();
  const balanceStore = useBalanceStore();
  const uploadRef = ref<UploadInstance>();
  const uploading = ref(false);
  const generating = ref(false);
  const looping = ref(false);
  const loopIndex = ref(0);
  const progressText = ref('生成中...');
  const previewImageUrl = ref('');
  const uploadedImageUrl = ref('');
  const characterName = ref('');
  const characterDescription = ref('');
  const selectedStyle = ref('水彩童话');
  const currentStyle = ref('');
  const generatedResults = ref<GeneratedResult[]>([]);
  let previewObjectUrl = '';

  const artStyles: ArtStyleOption[] = [
    { value: '国风动画', label: '国风动画' },
    { value: '水彩童话', label: '水彩童话' },
    { value: '蜡笔涂鸦', label: '蜡笔涂鸦' },
    { value: '卡通简约', label: '卡通简约' },
    { value: '拼贴纸艺', label: '拼贴纸艺' },
    { value: '水墨国风', label: '水墨国风' },
    { value: '国风3D', label: '国风3D' },
    { value: '森林童话', label: '森林童话' },
    { value: '扁平几何', label: '扁平几何' },
    { value: '粉彩柔雾', label: '粉彩柔雾' },
    { value: '日式手绘', label: '日式手绘' },
    { value: 'Q版3D', label: 'Q版3D' },
    { value: '油画厚涂', label: '油画厚涂' },
    { value: '黏土定格', label: '黏土定格' }
  ];

  const hasTextDesignInput = computed(
    () => Boolean(characterName.value.trim() && characterDescription.value.trim())
  );
  const canGenerate = computed(
    () =>
      !generating.value &&
      !looping.value &&
      !uploading.value &&
      (Boolean(uploadedImageUrl.value) || hasTextDesignInput.value)
  );
  const canLoop = computed(() => canGenerate.value);
  const currentResult = computed(() => {
    if (currentStyle.value) {
      const matched = generatedResults.value.find((result) => result.style === currentStyle.value);
      if (matched) return matched;
    }
    return generatedResults.value[0] ?? null;
  });

  const handleUploadChange = async (uploadFile: UploadFile) => {
    const file = uploadFile.raw;
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      ElMessage.warning('请上传图片格式文件');
      uploadRef.value?.clearFiles();
      return;
    }

    revokePreviewObjectUrl();
    previewObjectUrl = URL.createObjectURL(file);
    previewImageUrl.value = previewObjectUrl;
    uploadedImageUrl.value = '';
    generatedResults.value = [];
    currentStyle.value = '';
    uploading.value = true;

    try {
      uploadedImageUrl.value = await uploadCharacterReferenceImage(file);
      ElMessage.success('图片上传成功');
    } catch (error) {
      ElMessage.error(getErrorMessage(error, '图片上传失败，请重新上传'));
    } finally {
      uploading.value = false;
      uploadRef.value?.clearFiles();
    }
  };

  const validateGenerateInput = () => {
    if (uploadedImageUrl.value) return true;
    if (!characterName.value.trim()) {
      ElMessage.warning('未上传图片时，请填写角色名称');
      return false;
    }
    if (!characterDescription.value.trim()) {
      ElMessage.warning('未上传图片时，请填写角色设定');
      return false;
    }
    return true;
  };

  const generateSelectedStyle = async () => {
    if (!validateGenerateInput()) return;

    generating.value = true;
    try {
      const result = await generateStyle(selectedStyle.value);
      upsertGeneratedResult(result);
      currentStyle.value = result.style;
      ElMessage.success(uploadedImageUrl.value ? '角色风格化完成' : '角色形象设计完成');
    } catch (error) {
      ElMessage.error(
        getErrorMessage(error, uploadedImageUrl.value ? '角色风格化失败，请稍后重试' : '角色设计失败，请稍后重试')
      );
    } finally {
      generating.value = false;
      progressText.value = '生成中...';
    }
  };

  const generateAllStyles = async () => {
    if (!validateGenerateInput()) return;

    looping.value = true;
    loopIndex.value = 0;

    try {
      for (const style of artStyles) {
        loopIndex.value += 1;
        selectedStyle.value = style.value;
        progressText.value = `${style.label}生成中...`;

        try {
          const result = await generateStyle(style.value);
          upsertGeneratedResult(result);
          currentStyle.value = result.style;
        } catch (error) {
          ElMessage.error(`${style.label}生成失败：${getErrorMessage(error, '请稍后重试')}`);
        }
      }
      ElMessage.success('全部风格循环生成完成');
    } finally {
      looping.value = false;
      loopIndex.value = 0;
      progressText.value = '生成中...';
    }
  };

  const generateStyle = async (style: string): Promise<GeneratedResult> => {
    const styleOption = getStyleOption(style);
    const name = characterName.value.trim();
    const description = characterDescription.value.trim();

    const submitted = uploadedImageUrl.value
      ? await stylizeCharacter({
          image_url: uploadedImageUrl.value,
          name: name || undefined,
          description: description || undefined,
          art_style: style
        })
      : await designCharacter({
          name,
          description,
          art_style: style
        });

    const task = await pollTask<CharacterStylizeResult | CharacterDesignResult>(submitted.task_id, {
      intervalMs: 2500,
      timeoutMs: 900000,
      onUpdate: (snapshot) => updateProgressText(snapshot, styleOption.label)
    });
    void balanceStore.refreshBalance();

    if (task.status === 'failed') {
      throw new Error(
        task.error || (uploadedImageUrl.value ? '角色风格化失败，请稍后重试' : '角色设计失败，请稍后重试')
      );
    }

    const imageUrl = task.result?.image_url;
    if (!imageUrl) throw new Error('任务完成但未返回图片地址');

    return {
      style,
      styleLabel: styleOption.label,
      imageUrl,
      prompt: task.result?.prompt ?? ''
    };
  };

  const updateProgressText = (
    snapshot: TaskSnapshot<CharacterStylizeResult | CharacterDesignResult>,
    styleLabel: string
  ) => {
    const percent = Number.isFinite(snapshot.progress) ? Math.round(snapshot.progress * 100) : 0;
    progressText.value =
      snapshot.message || `${styleLabel}生成中${percent ? ` ${percent}%` : '...'}`;
  };

  const upsertGeneratedResult = (result: GeneratedResult) => {
    const index = generatedResults.value.findIndex((item) => item.style === result.style);
    if (index >= 0) {
      generatedResults.value.splice(index, 1, result);
      return;
    }
    generatedResults.value.push(result);
  };

  const downloadResult = async (result: GeneratedResult) => {
    await downloadImage(result.imageUrl, buildDownloadFilename(result));
  };

  const downloadAllResults = async () => {
    if (!generatedResults.value.length) return;

    for (const result of generatedResults.value) {
      await downloadResult(result);
      await delay(180);
    }
  };

  const downloadImage = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('图片下载失败');
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      triggerDownload(objectUrl, filename);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      triggerDownload(url, filename);
    }
  };

  const triggerDownload = (href: string, filename: string) => {
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const buildDownloadFilename = (result: GeneratedResult) =>
    `character-${sanitizeFilename(result.styleLabel)}.png`;

  const sanitizeFilename = (value: string) => value.replace(/[\\/:*?"<>|]/g, '-');

  const getStyleOption = (style: string) =>
    artStyles.find((item) => item.value === style) ?? { value: style, label: style };

  const getErrorMessage = (error: unknown, fallback: string) => {
    if (error instanceof Error && error.message) return error.message;
    if (typeof error === 'object' && error !== null) {
      const record = error as Record<string, unknown>;
      if (typeof record.message === 'string') return record.message;
      if (typeof record.error === 'string') return record.error;
    }
    return fallback;
  };

  const revokePreviewObjectUrl = () => {
    if (!previewObjectUrl) return;
    URL.revokeObjectURL(previewObjectUrl);
    previewObjectUrl = '';
  };

  const delay = (ms: number) =>
    new Promise<void>((resolve) => {
      window.setTimeout(resolve, ms);
    });

  onBeforeUnmount(() => {
    revokePreviewObjectUrl();
  });
</script>

<style scoped>
  .test-character-page {
    width: 100%;
    height: 100vh;
    overflow-y: auto;
    padding: 40px;
    box-sizing: border-box;
    background: #fbf9ff;
    color: #160211;
  }

  .test-character-header {
    max-width: 1280px;
    margin: 0 auto 28px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
  }

  .eyebrow {
    margin: 0 0 8px;
    color: #6033e1;
    font-size: 13px;
    font-weight: 800;
    text-transform: uppercase;
  }

  .test-character-header h1 {
    margin: 0;
    font-size: 30px;
  }

  .summary {
    max-width: 760px;
    margin: 12px 0 0;
    color: #6e6a7c;
    line-height: 1.7;
  }

  .test-character-header button,
  .primary-action,
  .secondary-action,
  .result-copy button,
  .grid-copy button,
  .download-all-button {
    height: 40px;
    padding: 0 18px;
    border: 0;
    border-radius: 12px;
    background: #6033e1;
    color: #fff;
    font-weight: 800;
    cursor: pointer;
    transition:
      transform 0.15s,
      opacity 0.15s;
  }

  .test-character-header button:hover,
  .primary-action:hover,
  .secondary-action:hover,
  .result-copy button:hover,
  .grid-copy button:hover,
  .download-all-button:hover {
    transform: translateY(-1px);
  }

  button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  .test-character-content {
    max-width: 1280px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .panel {
    padding: 22px;
    border-radius: 18px;
    background: #fff;
    box-shadow: 0 14px 40px rgba(73, 56, 130, 0.08);
  }

  .section-title {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 18px;
  }

  .section-title h2 {
    margin: 0;
    font-size: 20px;
  }

  .section-title span {
    color: #6e6a7c;
    font-size: 13px;
    font-weight: 700;
  }

  .input-grid {
    display: grid;
    grid-template-columns: minmax(320px, 460px) 1fr;
    gap: 20px;
  }

  .image-upload :deep(.el-upload) {
    width: 100%;
  }

  .image-upload :deep(.el-upload-dragger) {
    width: 100%;
    height: 390px;
    padding: 0;
    overflow: hidden;
    border: 1px dashed #d8d0f2;
    border-radius: 18px;
    background: #fbf9ff;
  }

  .image-upload :deep(.el-upload-dragger:hover),
  .image-upload :deep(.el-upload-dragger.is-dragover) {
    border-color: #6033e1;
    background: #f5f1ff;
  }

  .upload-content {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: #6e6a7c;
  }

  .upload-content.has-image {
    position: relative;
    background: #f5f1ff;
  }

  .upload-icon {
    width: 64px;
    height: 64px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 20px;
    background: #ede8ff;
    color: #6033e1;
    font-size: 36px;
    font-weight: 500;
  }

  .upload-content strong {
    color: #160211;
    font-size: 16px;
  }

  .upload-content small {
    color: #9b94a8;
    font-size: 13px;
  }

  .uploaded-preview-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .change-upload-button {
    position: absolute;
    left: 50%;
    bottom: 18px;
    transform: translateX(-50%);
    padding: 9px 16px;
    border-radius: 999px;
    background: rgba(96, 51, 225, 0.92);
    color: #fff;
    font-size: 13px;
    font-weight: 800;
  }

  .form-card {
    min-height: 390px;
    padding: 20px;
    border: 1px solid #eeeaf8;
    border-radius: 18px;
    background: #fff;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .form-card label {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .form-card label > span {
    color: #160211;
    font-size: 14px;
    font-weight: 800;
  }

  .style-select {
    width: 100%;
  }

  .form-card :deep(.el-input__wrapper),
  .form-card :deep(.el-textarea__inner),
  .form-card :deep(.el-select__wrapper) {
    border-radius: 12px;
    box-shadow: 0 0 0 1px #eeeaf8 inset;
  }

  .form-card :deep(.el-textarea__inner) {
    min-height: 104px !important;
  }

  .actions {
    margin-top: auto;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  .secondary-action,
  .download-all-button {
    background: #f3efff;
    color: #6033e1;
  }

  .current-result-card {
    display: grid;
    grid-template-columns: minmax(320px, 460px) 1fr;
    gap: 20px;
    border: 1px solid #eeeaf8;
    border-radius: 18px;
    overflow: hidden;
  }

  .image-box {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f1ff;
  }

  .image-box img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .result-image {
    height: 420px;
  }

  .result-copy {
    padding: 22px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
  }

  .result-style {
    margin: 0;
    color: #6033e1;
    font-size: 18px;
    font-weight: 900;
  }

  .result-prompt {
    max-height: 240px;
    margin: 0;
    overflow-y: auto;
    color: #6e6a7c;
    line-height: 1.7;
  }

  .empty-result,
  .empty-gallery {
    min-height: 180px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 1px dashed #d8d0f2;
    border-radius: 18px;
    background: #fbf9ff;
    color: #8d8799;
    text-align: center;
  }

  .empty-result h3 {
    margin: 0 0 8px;
    color: #160211;
  }

  .empty-result p {
    max-width: 420px;
    margin: 0;
    line-height: 1.7;
  }

  .result-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 18px;
  }

  .result-card {
    overflow: hidden;
    border: 1px solid #eeeaf8;
    border-radius: 14px;
    background: #fff;
    cursor: pointer;
    transition:
      border-color 0.15s,
      transform 0.15s,
      box-shadow 0.15s;
  }

  .result-card:hover,
  .result-card.active {
    border-color: #6033e1;
    transform: translateY(-2px);
    box-shadow: 0 14px 32px rgba(73, 56, 130, 0.12);
  }

  .grid-image {
    height: 220px;
  }

  .grid-copy {
    padding: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .grid-copy strong {
    color: #160211;
  }

  .grid-copy button {
    height: 32px;
    padding: 0 12px;
    border-radius: 10px;
    font-size: 12px;
  }

  @media (max-width: 900px) {
    .test-character-page {
      padding: 24px 16px;
    }

    .test-character-header,
    .input-grid,
    .current-result-card {
      grid-template-columns: 1fr;
    }

    .test-character-header {
      flex-direction: column;
    }
  }
</style>
